/**
 * The document write service.
 *
 * THIS IS THE ONLY PLACE A TRANSACTION IS OPENED FOR A DOCUMENT.
 * An ESLint rule bans `dexie` imports from `ui/` and `features/`, so every
 * multi-table write funnels through here — which is what makes "issue an
 * invoice" atomic instead of a sequence of half-writes.
 *
 * ONE TRANSACTION COVERS
 *   1. allocate the number
 *   2. re-validate availability against the source document
 *   3. write the header
 *   4. write the lines
 *   5. write the source graph (links)
 *   6. post stock movements
 *   7. roll up fulfilment counters on the source lines
 *   8. append the audit row
 *
 * A failure at any step leaves NOTHING behind. That property is asserted by
 * `documentService.test.ts`, which forces a throw mid-way and then checks
 * the database is byte-identical to before.
 */
import type { Table } from 'dexie';
import { computeDocument } from '@core/calc';
import type { DocType } from '@domain/docTypes';
import { docSpec } from '@domain/docTypes';
import type { DocStatus } from '@domain/lifecycle';
import { canIssue } from '@domain/lifecycle';
import {
  checkConsumption,
  getLineAvailability,
  type LineAvailability,
} from '@domain/fulfilment';
import { movementReasonFor, stockOwnerFor } from '@domain/stockPolicy';
import type {
  AuditLog,
  Document,
  DocumentLine,
  DocumentLink,
  PartySnapshot,
  StockTransaction,
  SyncOutboxEntry,
} from '../db/types';
import { allocateDocNumber, type NumberFormat } from '../repositories/sequences';
import { nextVersion, recordBusinessMutations } from '../sync/recordBusinessMutation';
import { ulid } from '../../platform/ids';

export class DocumentServiceError extends Error {
  constructor(
    readonly code:
      | 'NOT_ISSUABLE'
      | 'OVER_CONSUMPTION'
      | 'MISSING_PARTY'
      | 'INVALID_STATE'
      | 'DUPLICATE_NUMBER'
      // The row an edit was asked to overwrite is gone. Distinct from the others
      // because the user's next action differs: nothing is wrong with what they
      // typed, the invoice they opened has simply been deleted or synced away
      // from under them, and "start again" is not the advice they need.
      | 'NOT_FOUND',
    message: string,
  ) {
    super(message);
    this.name = 'DocumentServiceError';
  }
}

export interface TxTables {
  documents: Table<Document, string>;
  documentLines: Table<DocumentLine, string>;
  documentLinks: Table<DocumentLink, string>;
  stockTransactions: Table<StockTransaction, string>;
  documentSequences: Table<{ id: string; nextValue: number; isSealed: boolean }, string>;
  auditLogs: Table<AuditLog, string>;
  parties: Table<{ id: string; stateCode: string; name: string; gstin?: string }, string>;
  businesses: Table<{ id: string; documentSettings: { allowInvoiceWithoutDelivery: boolean } }, string>;
  /**
   * The sync outbox, in the SAME transaction. See `PaymentTx.syncOutbox` for why
   * it is required rather than optional.
   */
  syncOutbox: Table<SyncOutboxEntry, string>;
  table(name: string): Table<Record<string, unknown>, string>;
}

export interface IssueLineInput {
  id?: string;
  productId?: string;
  stockLocationId?: string;
  description: string;
  descriptionExtra?: string;
  hsnSac?: string;
  unit: string;
  quantityMilli: number;
  freeQuantityMilli?: number;
  ratePaise: number;
  discountType?: 'NONE' | 'PERCENT' | 'AMOUNT';
  discountValue?: number;
  taxRateBp: number;
  cessRateBp?: number;
  /** Source line this line consumes, for partial fulfilment. */
  sourceLineId?: string;
  isStockTracked?: boolean;
}

export interface IssueParams {
  businessId: string;
  businessStateCode: string;
  docType: DocType;
  docDate: string;
  customerId?: string;
  supplierId?: string;
  partySnapshot: PartySnapshot;
  placeOfSupplyCode: string;
  /**
   * The TRANSACTION's delivery address, copied from the customer master.
   *
   * Stored on the document so the place of supply stays defensible: the goods
   * went to one place on one day, and neither the customer's later address edit
   * nor the frozen `placeOfSupplyCode` may disturb the other.
   */
  deliveryAddressLines?: string[];
  deliveryStateCode?: string;
  deliveryStateName?: string;
  reverseCharge?: boolean;
  lines: IssueLineInput[];
  charges?: Document['charges'];
  /**
   * The foot of the document: notes, terms, and the declaration.
   *
   * SNAPSHOTTED ONTO THE ROW, never read from the business at print time, and
   * that is the whole point. The renderer prints `m.terms` — what this document
   * was issued with. If the business profile were consulted while printing, then
   * editing the terms today would silently rewrite the terms on every invoice
   * already issued, which is a tax document changing after the fact. The same
   * reasoning as `partySnapshot` and `placeOfSupplyCode`.
   *
   * Omitted keys are omitted from the row rather than written as `undefined`,
   * so "no terms" and "empty terms" are not two different stored states.
   */
  readonly notes?: string;
  readonly terms?: string;
  readonly declaration?: string;
  readonly coverLetter?: string;
  readonly coverLetterEnabled?: boolean;
  readonly expiryDate?: string;
  invoiceDiscount?: { type: 'NONE' | 'PERCENT' | 'AMOUNT'; value: number };
  roundOff?: boolean;
  fyStartMonth?: number;
  numberFormat?: Partial<NumberFormat>;
  originalDocId?: string;
  salesOrderId?: string;
  purchaseOrderId?: string;
  quotationId?: string;
  status?: DocStatus;
  /**
   * Write onto THIS row instead of creating a new one.
   *
   * THE ONLY CORRECT WAY TO EDIT A DRAFT, and it exists because the obvious
   * approach is wrong in a way no local test can see. Creating a throwaway
   * document and then moving it onto the original looks harmless in the local
   * database — and it is, every time. But `issueDocument` also writes an AUDIT
   * row and pushes a SYNC OUTBOX mutation keyed on the id it minted. Move the
   * header afterwards and the outbox still describes a document that no longer
   * exists, while the document that DOES exist is never published. On a second
   * device that means a ghost invoice the user cannot open, and an edited draft
   * that stays stale forever.
   *
   * Overriding the identity keeps the single write path AND the single identity:
   * the audit row, the outbox entry and the document row are all written against
   * the original id in the same transaction, so there is no window in which they
   * can disagree.
   *
   * `docNumber` travels with it because a draft already carries a number the user
   * has seen. Re-allocating would burn a second number per edit, leaving gaps in
   * GSTR-1 for invoices that never existed.
   *
   * Only ever set for a DRAFT. An issued document's identity is a tax record.
   */
  replaceIdentity?: { id: string; docNumber: string };
  user: { id: string; name: string };
  now: string;
  /**
   * This install's device id, so the mutation identifies where it came from.
   * Resolved once by the caller with `currentDeviceId(db)`.
   */
  deviceId: string;
  /** Test hook: throw after this many steps, to prove rollback. */
  failAfterStep?: number;
}

export interface IssueResult {
  id: string;
  docNumber: string;
  financialYear: string;
  grandTotalPaise: number;
  availability: LineAvailability[];
}

/** Map an editor line onto the pure calculation engine's input. */
function toCalcLine(l: IssueLineInput) {
  return {
    id: l.id,
    productId: l.productId,
    description: l.description,
    descriptionExtra: l.descriptionExtra,
    hsnSac: l.hsnSac,
    unit: l.unit,
    quantity: l.quantityMilli as never,
    freeQuantity: (l.freeQuantityMilli ?? 0) as never,
    ratePaise: l.ratePaise as never,
    discountType: l.discountType ?? 'NONE',
    discountValue: l.discountValue ?? 0,
    taxRateBp: l.taxRateBp as never,
    cessRateBp: (l.cessRateBp ?? 0) as never,
  };
}

/** Why a line may not consume its source. Throws with the exact reason. */
function assertCanConsume(
  target: 'DELIVERY' | 'INVOICE' | 'RECEIPT' | 'BILL' | 'RETURN',
  availability: LineAvailability,
  qtyMilli: number,
): void {
  const result = checkConsumption(target, availability, qtyMilli as never);
  if (result.ok) return;
  throw new DocumentServiceError('OVER_CONSUMPTION', result.error.message);
}

/**
 * Which capacity a document type consumes from its source.
 *
 * `RETURN` is not an optional case here. Without it, a Sales Return fell through
 * to the `default` arm and was validated against `deliverableQty` — the quantity
 * an ORDER still owes out. That number is unrelated to what was sold: a return
 * would be refused or permitted against the wrong figure, and on a fully
 * dispatched order it would be refused outright even though the goods were
 * definitely sold. `checkConsumption` already knew how to answer `RETURN` from
 * `returnableQty`; only this mapping failed to ask it.
 */
function consumptionTarget(docType: DocType): 'DELIVERY' | 'INVOICE' | 'RECEIPT' | 'BILL' | 'RETURN' {
  switch (docType) {
    case 'SALES_RETURN':
    case 'PURCHASE_RETURN':
      return 'RETURN';
    case 'DELIVERY_NOTE':
      return 'DELIVERY';
    case 'SALES_INVOICE':
      return 'INVOICE';
    case 'PURCHASE_RECEIPT':
      return 'RECEIPT';
    case 'PURCHASE_INVOICE':
      return 'BILL';
    default:
      return 'DELIVERY';
  }
}

/** Link relation recorded for a child document of this type. */
function linkRelationFor(docType: DocType): DocumentLink['relation'] {
  switch (docType) {
    case 'DELIVERY_NOTE':
      return 'DELIVERED_AGAINST';
    case 'SALES_INVOICE':
      return 'INVOICED_AGAINST';
    case 'PURCHASE_RECEIPT':
      return 'PURCHASE_RECEIVED_AGAINST';
    case 'PURCHASE_INVOICE':
      return 'PURCHASE_BILLED_AGAINST';
    // A CREDIT NOTE consumes the same returnable pool as a physical return.
    //
    // Without it, a credit note fell through to `CONVERTED_FROM`, and
    // `getLineAvailability` derives `returnableQty` by summing RETURNED_AGAINST
    // links only. A credit note would therefore have consumed NO capacity, so a
    // shop could credit the same quantity back twice — once as a credit note and
    // once as a return. The two types differ in whether STOCK moves, never in
    // whether they consume what was sold.
    case 'CREDIT_NOTE':
    case 'SALES_RETURN':
    case 'PURCHASE_RETURN':
      return 'RETURNED_AGAINST';
    default:
      return 'CONVERTED_FROM';
  }
}


/**
 * The `docType` of the document that owns each consumed source line.
 *
 * This is the lookup that separates "invoice against a Delivery Note" from
 * "invoice straight from a Sales Order". The link relation is identical for
 * both — `INVOICED_AGAINST` — so the link alone cannot tell them apart, and
 * without this the two are indistinguishable and every invoice double-deducts.
 *
 * Read through the LINES table rather than the links, because a line records the
 * document it came from and a link records the document it produced. Lines that
 * do not exist are simply absent from the map, which reads as "no source", and
 * the caller treats that as an invoice that may own the movement itself.
 */
async function loadSourceDocTypes(
  tx: Pick<TxTables, 'documentLines' | 'documents'>,
  lines: readonly IssueLineInput[],
): Promise<Map<string, DocType>> {
  const ids = lines.map((l) => l.sourceLineId).filter((id): id is string => Boolean(id));
  if (ids.length === 0) return new Map();

  const found = new Map<string, DocType>();
  for (const id of ids) {
    const line = await tx.documentLines.get(id);
    if (!line) continue;
    const doc = await tx.documents.get(line.documentId);
    if (doc) found.set(id, doc.docType);
  }
  return found;
}


/**
 * Issue a document atomically.
 *
 * The caller passes the live `tx` so this runs inside a Dexie transaction
 * the caller opened, which is what makes rollback possible.
 */
export async function issueDocument(
  tx: TxTables,
  params: IssueParams,
): Promise<IssueResult> {
  const step = (n: number): void => {
    if (params.failAfterStep === n) throw new Error(`Injected failure after step ${n}`);
  };

  const status = params.status ?? 'DRAFT';
  const spec = docSpec(params.docType);

  // ── 1. PURE calculation, before any write ────────────────────────────
  const computed = computeDocument({
    business: { stateCode: params.businessStateCode, currency: 'INR' },
    docType: params.docType,
    docDate: params.docDate,
    financialYear: '',
    placeOfSupplyCode: params.placeOfSupplyCode,
    reverseCharge: params.reverseCharge ?? false,
    lines: params.lines.map(toCalcLine),
    // ChargeInput.taxRateBp is a branded BasisPoints; a persisted ChargeRow
    // stores a plain number. The brand is a compile-time guard, not a runtime
    // one, so widening here is safe — the engine re-derives every amount.
    charges: (params.charges ?? []).map((c) => ({
      label: c.label,
      type: c.type,
      value: c.value,
      taxable: c.taxable,
      ...(c.taxRateBp !== undefined ? { taxRateBp: c.taxRateBp as never } : {}),
    })),
    invoiceDiscount: params.invoiceDiscount ?? null,
    roundOff: params.roundOff ?? false,
    roundOffMode: 'NEAREST_RUPEE',
    tcs: null,
  });

  // ── 2. guards, before any write ───────────────────────────────────────
  const guard = canIssue({
    docType: params.docType,
    status,
    lineCount: params.lines.length,
    hasCounterparty: Boolean(params.customerId ?? params.supplierId),
    allocatedPaise: 0,
    isReferenced: false,
  });
  if (!guard.ok) throw new DocumentServiceError('NOT_ISSUABLE', guard.message);
  step(1);

  // ── 3. source availability, re-read inside the transaction ───────────
  const target = consumptionTarget(params.docType);
  const business = await tx.businesses.get(params.businessId);
  const availability: LineAvailability[] = [];
  for (const line of params.lines) {
    if (!line.sourceLineId) continue;
    const sourceLine = await tx.documentLines.get(line.sourceLineId);
    if (!sourceLine) continue;
    const links = await tx.documentLinks
      .where('[toDocumentId+toLineId]')
      .equals([sourceLine.documentId, sourceLine.id])
      .toArray();
    const a = getLineAvailability(
      {
        lineId: sourceLine.id,
        quantity: sourceLine.quantityMilli as never,
        // A line ON AN INVOICE has invoiced its own quantity.
        //
        // `invoicedQtyMilli` counts how much a LATER document invoiced against
        // this line, so on an invoice line it is legitimately zero — the invoice
        // is the top of that chain, not something beneath it. Reading it as the
        // "invoiced" figure therefore says the sale invoiced nothing, and every
        // RETURN against every invoice is refused with "exceeds the 0 available".
        //
        // Stating what the line actually sold is not a special case for returns:
        // it is the same quantity the fulfilment engine is being asked to measure
        // `returnableQty` from, so the guard and the screen now agree.
        invoicedQty: (sourceLine.docType === 'SALES_INVOICE'
          ? sourceLine.quantityMilli + sourceLine.freeQuantityMilli
          : sourceLine.invoicedQtyMilli) as never,
      },
      links
        .filter((link) => link.fromDocumentId !== params.replaceIdentity?.id)
        .map((link) => ({
          relation: link.relation,
          qty: link.qtyMilli as never,
          fromDocType: link.fromDocType,
        })),
      // The BUSINESS SETTING, read inside the transaction rather than assumed.
      // The cap an editor enforces and the movement a ledger posts must answer
      // the same question; a hard-coded 	rue here while the business says false
      // would let the screen offer an invoice the stock ledger then refuses to
      // move goods for.
      { allowInvoiceWithoutDelivery: business?.documentSettings.allowInvoiceWithoutDelivery ?? true },
    );
    assertCanConsume(target, a, line.quantityMilli + (line.freeQuantityMilli ?? 0));
    availability.push(a);
  }
  step(2);

  // ── 4. allocate the number (rolled back with everything else) ─────────
  //
  // An EDIT keeps the number it already had. A draft has been shown to the user
  // carrying a number, and re-allocating per edit would leave a gap in GSTR-1 for
  // every save — an invoice that never existed. The sequence is still NOT
  // decremented: the counter stays where the abandoned build left it, so no
  // number can ever be reused, and reuse is the one mistake that cannot be
  // corrected after the fact.
  const allocated = params.replaceIdentity
    ? null
    : await allocateDocNumber(tx as never, {
        businessId: params.businessId,
        docType: params.docType,
        docDate: params.docDate,
        issuedBy: params.user.id,
        issuedAt: params.now,
        fyStartMonth: params.fyStartMonth ?? 4,
        format: params.numberFormat,
      });
  step(3);

  // ── 5. header ─────────────────────────────────────────────────────────
  //
  // On an EDIT the original row is read ONCE, here, and everything identity-
  // related is taken from it: the id, the number, the sequence position, the
  // financial year, and the creation stamp. Reading it once matters — these are
  // five separate uses, and a second read would be five chances to observe a row
  // that has already been overwritten by this same statement.
  const original = params.replaceIdentity
    ? await tx.documents.get(params.replaceIdentity.id)
    : undefined;
  if (params.replaceIdentity && !original) {
    throw new DocumentServiceError(
      'NOT_FOUND',
      'That invoice no longer exists, so the edit was not saved.',
    );
  }
  // The DRAFT guard, enforced HERE as well as in `updateSalesInvoiceDraft`.
  //
  // Deliberately duplicated. `replaceIdentity` is reachable from the public
  // `IssueSalesInvoiceInput`, so the caller that sets it cannot be trusted to
  // have checked — and this is the one property that must not depend on a
  // caller remembering. An issued invoice's id, number and figures are a tax
  // record: overwriting one rewrites what the customer holds and what GSTR-1
  // reported, with no cancellation anywhere in the trail.
  if (original && original.status !== 'DRAFT') {
    throw new DocumentServiceError(
      'INVALID_STATE',
      `Invoice ${original.docNumber} is already ${original.status.toLowerCase()} and cannot ` +
        'be edited. Cancel it and raise a new one.',
    );
  }

  const docId = params.replaceIdentity?.id ?? ulid();
  // One number source, chosen explicitly rather than by a chain of `??` that
  // silently picks whichever happens to be defined.
  //
  // The two are mutually exclusive BY CONSTRUCTION: a create allocates a number
  // and has no original row; an edit has an original row and allocates nothing,
  // because re-allocating would burn a number the user has already been shown.
  // "Neither" is therefore unreachable, and it throws rather than assuming —
  // an empty `docNumber` here would produce an invoice numbered `INV/2026-27/0`,
  // which is worse than a clear failure.
  if (!original && !allocated) {
    throw new DocumentServiceError(
      'INVALID_STATE',
      'The invoice could not be numbered. Nothing was saved.',
    );
  }
  // Both branches are read through the SAME field names, so this is one object
  // rather than two shapes the caller has to remember. `original` is a full
  // `Document` and carries `docNumberSeq`, not `seq`; mapping it here keeps that
  // difference from leaking into the header literal below.
  const number = {
    docNumber: original?.docNumber ?? allocated?.docNumber ?? '',
    seq: original?.docNumberSeq ?? allocated?.seq ?? 0,
    financialYear: original?.financialYear ?? allocated?.financialYear ?? '',
  };
  const header: Document = {
    id: docId,
    businessId: params.businessId,
    docType: params.docType,
    docNumber: number.docNumber,
    docNumberSeq: number.seq,
    financialYear: number.financialYear,
    docDate: params.docDate,
    customerId: params.customerId,
    supplierId: params.supplierId,
    partySnapshot: params.partySnapshot,
    // The TRANSACTION's own copy of the delivery address, taken at creation and
    // then owned by the document. Stored so the frozen placeOfSupplyCode below
    // can be explained later: a POS with no recorded delivery state is an answer
    // nobody can audit.
    ...(params.deliveryAddressLines?.length
      ? { deliveryAddressLines: params.deliveryAddressLines }
      : {}),
    ...(params.deliveryStateCode ? { deliveryStateCode: params.deliveryStateCode } : {}),
    ...(params.deliveryStateName ? { deliveryStateName: params.deliveryStateName } : {}),
    placeOfSupplyCode: params.placeOfSupplyCode,
    supplyType: computed.supplyType,
    reverseCharge: params.reverseCharge ?? false,
    quotationId: params.quotationId,
    salesOrderId: params.salesOrderId,
    purchaseOrderId: params.purchaseOrderId,
    originalDocId: params.originalDocId,
    linkedChildDocIds: [],
    grossSubtotalPaise: computed.grossSubtotalPaise,
    lineDiscountPaise: computed.lineDiscountPaise,
    invoiceDiscountPaise: computed.invoiceDiscountPaise,
    totalDiscountPaise: computed.totalDiscountPaise,
    taxablePaise: computed.taxablePaise,
    taxableChargesPaise: computed.taxableChargesPaise,
    nonTaxableChargesPaise: computed.nonTaxableChargesPaise,
    cgstPaise: computed.cgstPaise,
    sgstPaise: computed.sgstPaise,
    igstPaise: computed.igstPaise,
    cessPaise: computed.cessPaise,
    tcsPaise: computed.tcsPaise,
    taxTotalPaise: computed.taxTotalPaise,
    preRoundOffPaise: computed.preRoundOffPaise,
    roundOffPaise: computed.roundOffPaise,
    grandTotalPaise: computed.grandTotalPaise,
    totalQtyMilli: computed.totalQty,
    totalFreeQtyMilli: computed.totalFreeQty,
    amountInWords: computed.amountInWords,
    charges: params.charges ?? [],
    // Absent keys are OMITTED, not written as undefined: "no terms on this
    // invoice" and "terms that happen to be empty" are different facts, and the
    // renderer must be able to tell them apart by whether the row carries one.
    ...(params.notes ? { notes: params.notes } : {}),
    ...(params.terms ? { terms: params.terms } : {}),
    ...(params.declaration ? { declaration: params.declaration } : {}),
    ...(params.coverLetter !== undefined ? { coverLetter: params.coverLetter } : {}),
    ...(params.coverLetterEnabled !== undefined ? { coverLetterEnabled: params.coverLetterEnabled } : {}),
    ...(params.expiryDate ? { expiryDate: params.expiryDate } : {}),
    status,
    allocatedPaise: 0,
    balancePaise: computed.grandTotalPaise,
    calcVersion: computed.calcVersion,
    calcSnapshot: JSON.stringify(computed),
    // An edit keeps the stamp the draft was FIRST saved under. `createdAt` is
    // what "when did this invoice start" means, and re-stamping it on every save
    // would make a two-week-old draft claim to have been raised today.
    createdAt: original?.createdAt ?? params.now,
    createdBy: original?.createdBy ?? params.user.id,
    updatedAt: params.now,
    updatedBy: params.user.id,
    // The row STARTS at version 1, and this is load-bearing rather than tidy.
    // The change id is qualified by the version, so the next operation derives
    // `document:<id>:v2` from THIS field. Without it the row reads as version 0,
    // the next write also computes 1, and the second change is collapsed by the
    // provider as a duplicate of the first — an issued invoice that never issues
    // anywhere else, with the local copy looking flawless.
    //
    // An EDIT INCREMENTS it. This is the same property, used for the same reason:
    // the draft's original create and this edit are two different logical
    // changes, and reusing `v1` would let the provider's dedup swallow the edit —
    // so the second device would keep the pre-edit draft indefinitely, with no
    // error anywhere.
    syncVersion: (original?.syncVersion ?? 0) + 1,
  };
  // `put`, not `add`. On a create the id is new and the two behave identically;
  // on an EDIT the row already exists, and `add` would abort the whole
  // transaction with a ConstraintError — so the user's edit would fail with a
  // message about a duplicate key, which describes the wrong problem entirely.
  await tx.documents.put(header);
  step(4);


  // ── 6. lines ─────────────────────────────────────────────────────────
  //
  // Preserve the old child rows long enough to publish tombstones. Physical
  // deletion without an outbox DELETE leaves stale lines and links on peers.
  const replacedLines = original
    ? await tx.documentLines.where('documentId').equals(docId).toArray()
    : [];
  const replacedLinks = original
    ? await tx.documentLinks.where('fromDocumentId').equals(docId).toArray()
    : [];
  const incomingLinks = original
    ? await tx.documentLinks
        .where('toDocumentId')
        .equals(docId)
        .and((link) => link.fromDocumentId !== docId)
        .toArray()
    : [];
  if (incomingLinks.length > 0) {
    throw new DocumentServiceError(
      'INVALID_STATE',
      'This draft is already referenced by another document and cannot be edited.',
    );
  }

  // On an EDIT the previous lines are removed FIRST. `bulkAdd` APPENDS, so
  // writing the new lines without this would leave the old ones in place under
  // the same document — and the header, which describes only the new lines,
  // would then disagree with the rows beneath it. `normalizeDocument` refuses
  // exactly that, so the save would appear to succeed and the invoice would
  // become un-previewable.
  //
  // The links go too, for the same reason and in the same breath: a
  // DELIVERED_AGAINST link left over from the pre-edit draft would claim the new
  // lines consume a Delivery Note they never consumed, and stock that belongs to
  // the note would silently never be deducted.
  if (original) {
    await tx.documentLines.where('documentId').equals(docId).delete();
    await tx.documentLinks.where('fromDocumentId').equals(docId).delete();
  }
  const rows: DocumentLine[] = params.lines.map((l, i) => {
    const c = computed.lines[i];
    return {
      id: ulid(),
      businessId: params.businessId,
      documentId: docId,
      docType: params.docType,
      lineNo: i + 1,
      productId: l.productId,
      stockLocationId: l.stockLocationId,
      description: l.description,
      descriptionExtra: l.descriptionExtra,
      hsnSac: l.hsnSac,
      unit: l.unit,
      quantityMilli: l.quantityMilli,
      freeQuantityMilli: l.freeQuantityMilli ?? 0,
      ratePaise: l.ratePaise,
      discountType: l.discountType ?? 'NONE',
      discountValue: l.discountValue ?? 0,
      discountPaise: c?.discountPaise ?? 0,
      allocatedDocDiscountPaise: c?.allocatedDocDiscountPaise ?? 0,
      taxablePaise: c?.taxablePaise ?? 0,
      taxRateBp: l.taxRateBp,
      cgstRateBp: c?.cgstRateBp ?? 0,
      sgstRateBp: c?.sgstRateBp ?? 0,
      igstRateBp: c?.igstRateBp ?? 0,
      cgstPaise: c?.cgstPaise ?? 0,
      sgstPaise: c?.sgstPaise ?? 0,
      igstPaise: c?.igstPaise ?? 0,
      cessRateBp: l.cessRateBp ?? 0,
      cessPaise: c?.cessPaise ?? 0,
      totalPaise: c?.totalPaise ?? 0,
      rateWithTaxPaise: c?.rateWithTaxPaise ?? 0,
      fulfilledQtyMilli: 0,
      invoicedQtyMilli: 0,
      returnedQtyMilli: 0,
      isActive: true,
    };
  });
  await tx.documentLines.bulkAdd(rows);
  step(5);

  // ── 7. source graph ──────────────────────────────────────────────────
  const links: DocumentLink[] = [];
  for (let i = 0; i < params.lines.length; i++) {
    const l = params.lines[i];
    const row = rows[i];
    if (!l?.sourceLineId || !row) continue;
    const src = await tx.documentLines.get(l.sourceLineId);
    if (!src) continue;
    links.push({
      id: ulid(),
      businessId: params.businessId,
      fromDocumentId: docId,
      fromLineId: row.id,
      fromDocType: params.docType,
      toDocumentId: src.documentId,
      toLineId: src.id,
      relation: linkRelationFor(params.docType),
      qtyMilli: l.quantityMilli + (l.freeQuantityMilli ?? 0),
      createdAt: params.now,
    });
  }
  if (links.length) await tx.documentLinks.bulkAdd(links);
  step(6);

  // ── 8. stock movements (append-only) ─────────────────────────────────
  // OWNER DECISION: a DRAFT never moves stock, and a document that consumes a
  // Delivery Note does not move it twice. `stockOwnerFor` decides both, and
  // `issueDraftDocument` calls the SAME function when a draft is issued — so the
  // two write paths cannot drift and start double-deducting.
  //
  // The ownership is computed PER LINE, because one invoice can legitimately
  // have a line delivered against a Delivery Note and another delivered and
  // invoiced in one step. A single per-document answer would force one or the
  // other to be wrong.
  const sourceDocTypeByLineId = await loadSourceDocTypes(tx, params.lines);
  // Declared OUTSIDE the branch so the sync block below can see the rows that
  // were actually posted. An empty array when nothing moved is the correct
  // value: it means no movement, not an unknown one.
  const movements: StockTransaction[] = [];
  if (spec.movesStock && spec.stockDirection) {
    const sign = spec.stockDirection === 'IN' ? 1 : -1;
    for (let i = 0; i < rows.length; i++) {
      const l = params.lines[i];
      const row = rows[i];
      if (!l || !row || !l.productId || !l.stockLocationId) continue;
      if (l.isStockTracked === false) continue;
      // Already delivered, so the Delivery Note owns this movement.
      if (stockOwnerFor({
        docType: params.docType,
        status,
        sourceLineDocType: l.sourceLineId ? sourceDocTypeByLineId.get(l.sourceLineId) : null,
      }) !== 'SELF') continue;
      const qty = (l.quantityMilli + (l.freeQuantityMilli ?? 0)) * sign;
      if (qty === 0) continue;
      movements.push({
        id: ulid(),
        businessId: params.businessId,
        productId: l.productId,
        stockLocationId: l.stockLocationId,
        documentId: docId,
        documentLineId: row.id,
        documentType: params.docType,
        docDate: params.docDate,
        direction: spec.stockDirection,
        qtyChangeMilli: qty,
        ratePaise: l.ratePaise,
        reason: movementReasonFor(params.docType, spec.stockDirection),
        balanceAfterMilli: 0,
        createdAt: params.now,
        createdBy: params.user.id,
      });
    }
    if (movements.length) await tx.stockTransactions.bulkAdd(movements);
  }
  step(7);

  // ── 9. audit, in the SAME transaction ─────────────────────────────────
  await tx.auditLogs.add({
    id: ulid(),
    businessId: params.businessId,
    entityType: 'DOCUMENT',
    entityId: docId,
    action: status === 'DRAFT' ? 'CREATE' : 'ISSUE',
    after: JSON.stringify({
      docNumber: header.docNumber,
      grandTotalPaise: header.grandTotalPaise,
    }),
    amountPaise: header.grandTotalPaise,
    userId: params.user.id,
    userName: params.user.name,
    occurredAt: params.now,
  });
  step(8);

  // ── 10. SYNC, in the SAME transaction ──────────────────────────────────
  //
  // The document, its lines, its source links and its stock movements travel
  // TOGETHER or not at all. A device that received the header without its lines
  // would show an invoice with no items; one that received the header without
  // its DELIVERED_AGAINST link would let the same stock be invoiced twice.
  //
  // Each mutation receives a freshly minted ULID, persisted with its payload in
  // this transaction. Sync retries resend that stored outbox row and therefore
  // retain the original identity; they do not rerun this write path.
  //
  // The payload is the row that was actually written. No recomputation here — a
  // second calculation engine is the 1.6.x defect this project exists to kill
  // (ADR 0003), and the document's own figures are the ones already computed
  // and reconciled above.
  await recordBusinessMutations(tx, [
    {
      businessId: header.businessId,
      deviceId: params.deviceId,
      entityType: 'document',
      entityId: header.id,
      operation: 'UPSERT',
      // The row's ACTUAL new version, not a literal 1. On an edit that is 2, 3,
      // 4... and the receiving device needs the real number: it is what orders
      // this change against the one it already holds, and what the conflict
      // model reads when the same draft was edited on two devices. Hard-coding 1
      // would make every edit look like the original create.
      //
      // `?? 1` because `syncVersion` is optional on the row type — sparse rows
      // written by the 1.6.x importer carry no version. Such a row is treated as
      // version 1 by this expression above, so publishing 1 here is consistent
      // rather than a separate guess.
      version: header.syncVersion ?? 1,
      changedAt: params.now,
      payload: header as unknown as Record<string, unknown>,
    },
    ...replacedLines.map((line) => ({
      businessId: line.businessId,
      deviceId: params.deviceId,
      entityType: 'documentLine' as const,
      entityId: line.id,
      operation: 'DELETE' as const,
      version: nextVersion(line.syncVersion ?? 1),
      changedAt: params.now,
      deletedAt: params.now,
    })),
    ...replacedLinks.map((link) => ({
      businessId: link.businessId,
      deviceId: params.deviceId,
      entityType: 'documentLink' as const,
      entityId: link.id,
      operation: 'DELETE' as const,
      version: nextVersion(link.syncVersion ?? 1),
      changedAt: params.now,
      deletedAt: params.now,
    })),
    ...rows.map((line) => ({
      businessId: line.businessId,
      deviceId: params.deviceId,
      entityType: 'documentLine' as const,
      entityId: line.id,
      operation: 'UPSERT' as const,
      version: 1,
      changedAt: params.now,
      payload: line as unknown as Record<string, unknown>,
    })),
    ...links.map((link) => ({
      // The link's DIRECTION is in the row, and it must travel with it. A
      // DELIVERED_AGAINST link is what proves the Delivery Note already owned the
      // stock; losing the direction would let the receiving device double-deduct.
      businessId: link.businessId,
      deviceId: params.deviceId,
      entityType: 'documentLink' as const,
      entityId: link.id,
      operation: 'UPSERT' as const,
      version: 1,
      changedAt: params.now,
      payload: link as unknown as Record<string, unknown>,
    })),
    ...movements.map((movement) => ({
      businessId: movement.businessId,
      deviceId: params.deviceId,
      entityType: 'stockTransaction' as const,
      entityId: movement.id,
      operation: 'UPSERT' as const,
      version: 1,
      changedAt: params.now,
      payload: movement as unknown as Record<string, unknown>,
    })),
  ]);

  return {
    id: docId,
    docNumber: header.docNumber,
    financialYear: header.financialYear,
    grandTotalPaise: header.grandTotalPaise,
    availability,
  };
}
